import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { 
  Wrench, 
  Car, 
  Shield, 
  CreditCard, 
  Menu, 
  X,
  ChevronRight,
  BarChart3,
  MessageCircle,
  Globe
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import { useAuth } from '../contexts/AuthContext';

const HomePage: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useLanguage();
  const { isAuthenticated, isLoading } = useAuth();
  
  // Check localStorage too in case context is still loading
  const hasToken = !!localStorage.getItem('access_token');
  const hasUser = !!localStorage.getItem('user');
  
  console.log('HomePage - Auth check:', { isAuthenticated, isLoading, hasToken, hasUser });
  
  // If authenticated, redirect to dashboard
  if (isAuthenticated || (hasToken && hasUser && !isLoading)) {
    console.log('HomePage - User is authenticated, redirecting to dashboard');
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation - Desktop */}
      <nav className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <h1 className="text-2xl font-bold" style={{ color: '#008B8B' }}>Rayyi7</h1>
            </div>

            {/* Right side items */}
            <div className="flex items-center space-x-4">
              {/* Desktop Menu */}
              <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-700 hover:text-[#008B8B] transition">
                {t('nav.features')}
              </a>
              <a href="#about" className="text-gray-700 hover:text-[#008B8B] transition">
                {t('nav.about')}
              </a>
              <Link to="/pricing" className="text-gray-700 hover:text-[#008B8B] transition">
                {t('nav.pricing')}
              </Link>
              <Link
                to="/login"
                className="px-6 py-2 rounded-lg font-medium text-white transition"
                style={{ backgroundColor: '#FF8C00' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E67E00'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FF8C00'}
              >
                {t('nav.accessPlatform')}
              </Link>
              </div>

              {/* Language Toggle */}
              <LanguageToggle />

              {/* Mobile Menu Button */}
              <button
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6 text-gray-700" />
                ) : (
                  <Menu className="h-6 w-6 text-gray-700" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-3 space-y-3">
              <a 
                href="#features" 
                className="block text-gray-700 hover:text-[#008B8B] transition py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.features')}
              </a>
              <a 
                href="#about" 
                className="block text-gray-700 hover:text-[#008B8B] transition py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.about')}
              </a>
              <Link 
                to="/pricing" 
                className="block text-gray-700 hover:text-[#008B8B] transition py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.pricing')}
              </Link>
              <Link
                to="/login"
                className="block w-full text-center px-6 py-3 rounded-lg font-medium text-white transition"
                style={{ backgroundColor: '#FF8C00' }}
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.accessPlatform')}
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#008B8B] to-[#006666] opacity-5"></div>
        <div className="relative pt-16 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center lg:text-left lg:flex lg:items-center lg:justify-between">
              <div className="lg:w-1/2">
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                  {t('hero.title')} <span style={{ color: '#008B8B' }}>{t('hero.titleHighlight')}</span> {t('hero.titleEnd')}
                </h2>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  {t('hero.subtitle')}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  <Link
                    to="/login"
                    className="px-8 py-4 rounded-lg text-lg font-semibold text-white transition inline-flex items-center justify-center"
                    style={{ backgroundColor: '#FF8C00' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E67E00'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FF8C00'}
                  >
                    {t('hero.getStarted')}
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </Link>
                  <a
                    href="#demo"
                    className="px-8 py-4 rounded-lg text-lg font-semibold border-2 transition inline-flex items-center justify-center"
                    style={{ borderColor: '#008B8B', color: '#008B8B' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#008B8B';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#008B8B';
                    }}
                  >
                    {t('hero.watchDemo')}
                  </a>
                </div>
              </div>
              <div className="hidden lg:block lg:w-1/2 lg:pl-12">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#008B8B] to-[#FF8C00] opacity-10 rounded-3xl transform rotate-6"></div>
                  <img 
                    src="/api/placeholder/600/400" 
                    alt="Garage Management Dashboard" 
                    className="relative rounded-3xl shadow-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold" style={{ color: '#008B8B' }}>500+</div>
              <div className="text-gray-600 mt-1">{t('stats.activeGarages')}</div>
            </div>
            <div>
              <div className="text-3xl font-bold" style={{ color: '#FF8C00' }}>50K+</div>
              <div className="text-gray-600 mt-1">{t('stats.carsServiced')}</div>
            </div>
            <div>
              <div className="text-3xl font-bold" style={{ color: '#F59E0B' }}>98%</div>
              <div className="text-gray-600 mt-1">{t('stats.satisfaction')}</div>
            </div>
            <div>
              <div className="text-3xl font-bold" style={{ color: '#008B8B' }}>24/7</div>
              <div className="text-gray-600 mt-1">{t('stats.support')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {t('features.title')} <span style={{ color: '#008B8B' }}>{t('features.titleHighlight')}</span>
            </h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('features.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              icon={<Car className="w-12 h-12" style={{ color: '#008B8B' }} />}
              title={t('features.customerManagement')}
              description={t('features.customerManagementDesc')}
            />
            <FeatureCard
              icon={<Wrench className="w-12 h-12" style={{ color: '#FF8C00' }} />}
              title={t('features.jobTracking')}
              description={t('features.jobTrackingDesc')}
            />
            <FeatureCard
              icon={<MessageCircle className="w-12 h-12" style={{ color: '#F59E0B' }} />}
              title={t('features.whatsapp')}
              description={t('features.whatsappDesc')}
            />
            <FeatureCard
              icon={<CreditCard className="w-12 h-12" style={{ color: '#008B8B' }} />}
              title={t('features.payments')}
              description={t('features.paymentsDesc')}
            />
            <FeatureCard
              icon={<BarChart3 className="w-12 h-12" style={{ color: '#FF8C00' }} />}
              title={t('features.analytics')}
              description={t('features.analyticsDesc')}
            />
            <FeatureCard
              icon={<Shield className="w-12 h-12" style={{ color: '#F59E0B' }} />}
              title={t('features.security')}
              description={t('features.securityDesc')}
            />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:flex lg:items-center lg:gap-12">
            <div className="lg:w-1/2">
              <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                {t('about.title')} <span style={{ color: '#008B8B' }}>{t('about.titleHighlight')}</span>
              </h3>
              <p className="text-lg text-gray-600 mb-6">
                {t('about.description1')}
              </p>
              <p className="text-lg text-gray-600 mb-6">
                {t('about.description2')} <strong>{t('about.slickCompany')}</strong>{t('about.description3')}
              </p>
              <div className="flex items-center gap-6">
                <a 
                  href="https://slickecosystem.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-[#008B8B] hover:text-[#006666] font-medium"
                >
                  <Globe className="w-5 h-5 mr-2" />
                  {t('about.visitSlick')}
                </a>
              </div>
            </div>
            <div className="lg:w-1/2 mt-12 lg:mt-0">
              <div className="bg-gray-50 rounded-2xl p-8">
                <h4 className="text-2xl font-bold text-gray-900 mb-4">{t('about.whyChoose')}</h4>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <div className="w-6 h-6 rounded-full bg-[#10B981] flex-shrink-0 mt-0.5"></div>
                    <span className="ml-3 text-gray-700">{t('about.reason1')}</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 rounded-full bg-[#10B981] flex-shrink-0 mt-0.5"></div>
                    <span className="ml-3 text-gray-700">{t('about.reason2')}</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 rounded-full bg-[#10B981] flex-shrink-0 mt-0.5"></div>
                    <span className="ml-3 text-gray-700">{t('about.reason3')}</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 rounded-full bg-[#10B981] flex-shrink-0 mt-0.5"></div>
                    <span className="ml-3 text-gray-700">{t('about.reason4')}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{ backgroundColor: '#008B8B' }}>
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            {t('cta.title')}
          </h3>
          <p className="text-xl text-white opacity-90 mb-8">
            {t('cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/login"
              className="px-8 py-4 bg-white rounded-lg text-lg font-semibold transition inline-flex items-center justify-center"
              style={{ color: '#008B8B' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#F3F4F6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
              }}
            >
              {t('cta.startTrial')}
              <ChevronRight className="ml-2 h-5 w-5" />
            </Link>
            <a
              href="#contact"
              className="px-8 py-4 border-2 border-white text-white rounded-lg text-lg font-semibold transition hover:bg-white hover:text-[#008B8B]"
            >
              {t('cta.contactSales')}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h4 className="text-2xl font-bold mb-4" style={{ color: '#FF8C00' }}>Rayyi7</h4>
              <p className="text-gray-400">
                {t('footer.partOfSlick')}
              </p>
              <p className="text-gray-400 mt-2">
                {t('footer.ownedBy')}
              </p>
            </div>
            <div>
              <h5 className="font-semibold mb-4">{t('footer.product')}</h5>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#features" className="hover:text-white transition">{t('nav.features')}</a></li>
                <li><Link to="/pricing" className="hover:text-white transition">{t('nav.pricing')}</Link></li>
                <li><a href="#demo" className="hover:text-white transition">{t('hero.watchDemo')}</a></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold mb-4">{t('footer.company')}</h5>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#about" className="hover:text-white transition">{t('footer.aboutUs')}</a></li>
                <li><a href="https://slickecosystem.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition">Slick Ecosystem</a></li>
                <li><a href="#contact" className="hover:text-white transition">{t('cta.contactSales')}</a></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold mb-4">{t('footer.support')}</h5>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#help" className="hover:text-white transition">{t('footer.helpCenter')}</a></li>
                <li><a href="#docs" className="hover:text-white transition">{t('footer.documentation')}</a></li>
                <li><a href="tel:+96566477000" className="hover:text-white transition">+965 6647 7000</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-gray-800 text-center text-gray-400">
            <p>{t('footer.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const FeatureCard: React.FC<{ icon: React.ReactNode; title: string; description: string }> = ({ icon, title, description }) => {
  return (
    <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
      <div className="mb-4">{icon}</div>
      <h4 className="text-xl font-semibold text-gray-900 mb-3">{title}</h4>
      <p className="text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
};

export default HomePage;